import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useMemo, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ConfirmModal, MoreMenu, StateView, type MenuAction } from '@/card/record'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  LayerButtons,
  Notice,
  OnOffBadge,
  PrimaryButton,
  SaveBar,
  Switch,
  initials,
  isForbidden,
  useAdminEditing,
} from './AdminShell'
import {
  createAccessRole,
  deleteAccessRole,
  fetchAccessRoles,
  fetchAccessUsers,
  fetchRoleMatrix,
  saveRoleMatrix,
  updateAccessRole,
  type AccessRole,
  type MatrixRow,
  type Scope,
} from './api'
import { ACTION_ORDER, SECTION_ORDER, actionLabel, fieldLabel, resourceLabel, sectionKey } from './labels'

const SCOPES: Scope[] = ['own', 'department', 'all']
const SUPERADMIN = 'SUPERADMIN'
const SCOPE_CLASS: Record<Scope | '', string> = { '': 's-none', own: 's-own', department: 's-dept', all: 's-all' }

type Draft = Record<string, Scope | ''>
type Tab = 'records' | 'fields' | 'users'

/**
 * Роли и матрица прав (тикет #102, макет AdmRoles.dc.html).
 *
 * Матрица показывает и прямые права, и унаследованные от предков — вторые только для
 * чтения: снять унаследованное можно лишь у предка, и галочка, которая «снимается», но
 * остаётся в силе, учила бы не верить экрану. Отправляется матрица целиком: не отмеченное
 * право снимается, а не «остаётся как было».
 *
 * Права роли «Суперадмин» не правятся вовсе (сервер отвергает): ячейки показаны с замком,
 * а не полями, которые приведут к отказу.
 */
export function RolesPage() {
  const { t } = useApp()
  const editing = useAdminEditing('system.role')
  const queryClient = useQueryClient()
  const roles = useQuery({ queryKey: ['admin-roles'], queryFn: ({ signal }) => fetchAccessRoles(signal) })
  const [selected, setSelected] = useState<string | null>(null)
  const [creating, setCreating] = useState<{ copyOf: AccessRole | null } | null>(null)
  const [renaming, setRenaming] = useState<AccessRole | null>(null)
  const [removing, setRemoving] = useState<AccessRole | null>(null)
  const [dirty, setDirty] = useState(false)

  const list = roles.data ?? []
  const current = list.find((role) => role.id === selected) ?? list[0] ?? null
  const forbidden = isForbidden(roles.error)

  const remove = useMutation({
    mutationFn: (id: string) => deleteAccessRole(id),
    onSuccess: () => {
      editing.clear()
      setRemoving(null)
      setSelected(null)
      void queryClient.invalidateQueries({ queryKey: ['admin-roles'] })
    },
    onError: (error) => {
      setRemoving(null)
      editing.fail(error)
    },
  })

  const [switchTo, setSwitchTo] = useState<string | null>(null)
  const choose = (id: string) => {
    // Unsaved cells belong to the role they were set on; switching drops them — asked first.
    if (dirty && id !== current?.id) {
      setSwitchTo(id)
      return
    }
    setDirty(false)
    setSelected(id)
  }

  return (
    <AdminPage
      titleKey="adm.roles.title"
      leadKey="adm.roles.lead"
      readOnly={!editing.mayEdit}
      roKey="adm.roles.ro"
      forbidden={forbidden}
      primary={editing.mayEdit && (
        <PrimaryButton label={t('admin.roles.add')} quiet={dirty} onClick={() => setCreating({ copyOf: null })} />
      )}
    >
      <Notice text={editing.notice} />
      <AdminState
        isLoading={roles.isLoading}
        error={roles.error}
        onRetry={() => void roles.refetch()}
        forbiddenTitleKey="adm.roles.forbidden"
        errorTitleKey="adm.roles.error"
        skeleton={<RolesSkeleton />}
      >
        <div className="adm-split">
          <nav className="panel" aria-label={t('adm.roles.list')} style={{ overflow: 'hidden' }}>
            <div className="panel-h">
              <span className="h2">{t('adm.roles.list')}</span>
              <span className="count">{list.length}</span>
            </div>
            {list.map((role) => (
              <button
                key={role.id}
                type="button"
                className={role.id === current?.id ? 'role-item is-active' : 'role-item'}
                aria-current={role.id === current?.id ? 'page' : undefined}
                onClick={() => choose(role.id)}
              >
                <span className="ellipsis" style={{ fontWeight: 500 }}>{role.name}</span>
                <span className="t-sm muted nowrap">{role.holders}</span>
                <span className="mono t-xs muted ellipsis">{role.code}</span>
                <KindBadge role={role} small />
              </button>
            ))}
            <div className="panel-f muted">{t('adm.roles.systemNote')}</div>
          </nav>
          {current && (
            <RoleDetail
              key={current.id}
              role={current}
              roles={list}
              mayEdit={editing.mayEdit}
              onDirty={setDirty}
              menu={
                editing.mayEdit && current.code !== SUPERADMIN
                  ? [
                      { id: 'copy', label: t('adm.roles.copy'), icon: 'copy', onSelect: () => setCreating({ copyOf: current }) },
                      ...(current.system
                        ? []
                        : [{ id: 'rename', label: t('adm.roles.rename'), icon: 'adm-edit', onSelect: () => setRenaming(current) } as MenuAction]),
                      {
                        id: 'delete',
                        label: t('adm.roles.delete'),
                        icon: 'trash',
                        danger: true,
                        disabledReason: current.system
                          ? t('adm.roles.delete.system')
                          : current.holders > 0
                            ? t('adm.roles.delete.holders', { count: current.holders })
                            : undefined,
                        onSelect: () => setRemoving(current),
                      },
                    ]
                  : []
              }
            />
          )}
        </div>
      </AdminState>

      {creating && (
        <NewRoleModal roles={list} copyOf={creating.copyOf} onClose={() => setCreating(null)} onSaved={(role) => {
          setCreating(null)
          editing.clear()
          setDirty(false)
          setSelected(role.id)
          void queryClient.invalidateQueries({ queryKey: ['admin-roles'] })
        }} />
      )}
      {renaming && (
        <RenameRoleModal role={renaming} onClose={() => setRenaming(null)} onSaved={() => {
          setRenaming(null)
          void queryClient.invalidateQueries({ queryKey: ['admin-roles'] })
        }} />
      )}
      <ConfirmModal
        open={switchTo != null}
        icon="alert"
        title={t('adm.roles.discard')}
        confirmLabel={t('adm.savebar.reset')}
        onCancel={() => setSwitchTo(null)}
        onConfirm={() => {
          setDirty(false)
          setSelected(switchTo)
          setSwitchTo(null)
        }}
      />
      <ConfirmModal
        open={removing != null}
        title={t('adm.roles.deleteConfirm', { name: removing?.name ?? '' })}
        confirmLabel={t('adm.roles.delete')}
        pending={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing.id)}
      >
        <p>{t('adm.roles.deleteConfirm.text')}</p>
      </ConfirmModal>
    </AdminPage>
  )
}

function KindBadge({ role, small }: { role: AccessRole; small?: boolean }) {
  const { t } = useApp()
  const style = small ? { height: 18, fontSize: 11 } : undefined
  return role.system ? (
    <span className="badge" style={style}><Icon name="lock" />{t('adm.roles.kind.system')}</span>
  ) : (
    <span className="badge b-outline" style={style}>{t('adm.roles.kind.own')}</span>
  )
}

function RolesSkeleton() {
  const { t } = useApp()
  return (
    <div className="adm-split" aria-busy="true" aria-label={t('app.loading')}>
      <div className="panel" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {Array.from({ length: 10 }, (_, index) => (
          <div className="stack" style={{ gap: 6 }} key={index}>
            <span className="skel" style={{ width: `${50 + ((index * 19) % 40)}%` }} />
            <span className="skel" style={{ width: '40%', height: 8 }} />
          </div>
        ))}
      </div>
      <div className="stack" style={{ gap: 14 }}>
        <span className="skel" style={{ width: 280, height: 20 }} />
        <span className="skel" style={{ width: 420 }} />
        <span className="skel" style={{ height: 36, borderRadius: 6 }} />
      </div>
    </div>
  )
}

function RoleDetail({
  role,
  roles,
  mayEdit,
  menu,
  onDirty,
}: {
  role: AccessRole
  roles: AccessRole[]
  mayEdit: boolean
  menu: MenuAction[]
  onDirty: (dirty: boolean) => void
}) {
  const { t } = useApp()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<Tab>('records')
  const [notice, setNotice] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const locked = role.code === SUPERADMIN
  const editable = mayEdit && !locked
  const matrix = useQuery({
    queryKey: ['admin-role-matrix', role.id],
    queryFn: ({ signal }) => fetchRoleMatrix(role.id, signal),
  })
  const original = useMemo<Draft>(
    () => Object.fromEntries((matrix.data ?? []).map((row) => [row.permissionId, row.scope ?? ''])),
    [matrix.data],
  )
  // The draft remembers which matrix it was built from. The panel is reused across roles, so
  // for one render after a switch the new role's matrix met the previous role's draft: that
  // read as unsaved changes, and a click in that window opened "discard changes?" instead of
  // switching (a flaky test found it). A draft of another matrix counts as no draft at all.
  const [draft, setDraft] = useState<{ base: Draft; scopes: Draft } | null>(null)
  useEffect(() => setDraft({ base: original, scopes: original }), [original])
  const scopes = draft?.base === original ? draft.scopes : original
  const setScopes = (update: Draft | ((previous: Draft) => Draft)) =>
    setDraft((previous) => {
      const current = previous?.base === original ? previous.scopes : original
      return { base: original, scopes: typeof update === 'function' ? update(current) : update }
    })

  const rows = matrix.data ?? []
  const changed = rows.filter((row) => (scopes[row.permissionId] ?? '') !== (original[row.permissionId] ?? ''))
  useEffect(() => onDirty(changed.length > 0), [changed.length, onDirty])

  const save = useMutation({
    mutationFn: () =>
      saveRoleMatrix(
        role.id,
        Object.entries(scopes)
          .filter((entry): entry is [string, Scope] => entry[1] !== '')
          .map(([permissionId, scope]) => ({ permissionId, scope })),
      ),
    onSuccess: (rows) => {
      setNotice(null)
      setSaved(true)
      queryClient.setQueryData(['admin-role-matrix', role.id], rows)
    },
    onError: (error) => setNotice(error instanceof Error ? error.message : t('app.error')),
  })

  const scopeWord = (scope: Scope | '' | null) => (scope ? t(`admin.scope.${scope}`) : '—')
  const changes = changed.map((row) =>
    t('adm.roles.change', {
      what: `${resourceLabel(t, row.resource)} · ${row.field ? fieldLabel(t, row.field) : actionLabel(t, row.action)}`,
      from: scopeWord(original[row.permissionId] ?? ''),
      to: scopeWord(scopes[row.permissionId] ?? ''),
    }),
  )
  const recordRows = rows.filter((row) => row.field == null)
  const fieldRows = rows.filter((row) => row.field != null)
  const empty = rows.length > 0 && rows.every((row) => !row.scope && !row.inheritedScope)
  const visibleMenu = menu.filter((item) => item.id !== 'copy')
  const copy = menu.find((item) => item.id === 'copy')

  return (
    <section className="stack" style={{ gap: 12, minWidth: 0 }} aria-label={`${t('admin.roles.matrix')} ${role.code}`}>
      <div className="row" style={{ alignItems: 'flex-start', gap: 16 }}>
        <div className="stack" style={{ flex: 1, gap: 4 }}>
          <div className="row" style={{ gap: 8 }}>
            <h2 className="h1" style={{ fontSize: 18, lineHeight: '26px' }}>{role.name}</h2>
            <KindBadge role={role} />
          </div>
          <div className="rec-meta">
            <span className="mono t-sm">{role.code}</span>
            <span className="sep-dot" aria-hidden="true">·</span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTab('users')}>
              <Icon name="adm-users" size="sm" />
              {t('adm.roles.holders', { count: role.holders })}
            </button>
            {role.parentCode && (
              <>
                <span className="sep-dot" aria-hidden="true">·</span>
                <span>{t('adm.roles.inherits', { code: role.parentCode })}</span>
              </>
            )}
            {role.description && (
              <>
                <span className="sep-dot" aria-hidden="true">·</span>
                <span>{role.description}</span>
              </>
            )}
          </div>
        </div>
        {(copy || visibleMenu.length > 0) && (
          <div className="rec-actions">
            {copy && (
              <button type="button" className="btn btn-secondary" onClick={copy.onSelect}>
                <Icon name="copy" size="sm" />
                {copy.label}
              </button>
            )}
            {visibleMenu.length > 0 && <MoreMenu items={visibleMenu} label={t('adm.roles.more')} />}
          </div>
        )}
      </div>

      {locked && (
        <div className="alert alert-neutral" role="note">
          <Icon name="lock" />
          <span><span className="fg strong">{t('adm.roles.superadmin.title')}</span> {t('admin.roles.superadminLocked')}</span>
        </div>
      )}
      {notice && <Notice text={notice} />}
      {saved && changed.length === 0 && <p className="t-sm muted" role="status">{t('admin.roles.saved')}</p>}

      <AdminState isLoading={matrix.isLoading} error={matrix.error} onRetry={() => void matrix.refetch()}
                  errorTitleKey="adm.roles.error">
        {empty && changed.length === 0 && (
          <EmptyRole role={role} roles={roles} editable={editable} onCopy={(from) => {
            setSaved(false)
            setScopes({ ...scopes, ...from })
          }} />
        )}
        <div className="tabs" role="tablist" aria-label={t('adm.roles.tabs')}>
          {(['records', 'fields', 'users'] as const).map((key) => (
            <button key={key} type="button" role="tab" className="tab" aria-selected={tab === key} onClick={() => setTab(key)}>
              {t(`adm.roles.tab.${key}`)}
              {key === 'fields' && <span className="count-pill">{fieldRows.length}</span>}
              {key === 'users' && <span className="count-pill">{role.holders}</span>}
            </button>
          ))}
        </div>
        {tab === 'records' && (
          <RecordMatrix rows={recordRows} scopes={scopes} original={original} editable={editable} locked={locked}
                        onChange={(id, value) => {
                          setSaved(false)
                          setScopes({ ...scopes, [id]: value })
                        }} />
        )}
        {tab === 'fields' && (
          <FieldRights rows={fieldRows} scopes={scopes} editable={editable} locked={locked}
                       onChange={(id, value) => {
                         setSaved(false)
                         setScopes({ ...scopes, [id]: value })
                       }} />
        )}
        {tab === 'users' && <RoleUsers role={role} />}
        {editable && (
          <SaveBar
            changes={changes}
            pending={save.isPending}
            note={t('adm.roles.appliesTo', { count: role.holders })}
            onReset={() => setScopes(original)}
            onSave={() => save.mutate()}
          />
        )}
      </AdminState>
    </section>
  )
}

function EmptyRole({ role, roles, editable, onCopy }: {
  role: AccessRole
  roles: AccessRole[]
  editable: boolean
  onCopy: (draft: Draft) => void
}) {
  const { t } = useApp()
  const [source, setSource] = useState('')
  const copy = useMutation({
    mutationFn: (id: string) => fetchRoleMatrix(id),
    // Effective rights of the source — its own and inherited — become direct ones here:
    // the new role has no ancestor that would give them.
    onSuccess: (rows) => onCopy(Object.fromEntries(rows.map((row) => [row.permissionId, row.scope ?? row.inheritedScope ?? '']))),
  })
  return (
    <StateView
      boxed
      kind="empty"
      title={t('adm.roles.empty', { name: role.name })}
      description={t('adm.roles.empty.text')}
      actions={
        editable && (
          <>
            <select className="input input-sm" style={{ width: 240 }} aria-label={t('adm.roles.copyFrom')} value={source}
                    onChange={(event) => setSource(event.target.value)}>
              <option value="">{t('adm.roles.copyFrom')}</option>
              {roles.filter((candidate) => candidate.id !== role.id && candidate.code !== SUPERADMIN).map((candidate) => (
                <option key={candidate.id} value={candidate.id}>{candidate.name}</option>
              ))}
            </select>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!source || copy.isPending}
                    onClick={() => copy.mutate(source)}>
              {t('adm.roles.copyRights')}
            </button>
          </>
        )
      }
    />
  )
}

function ScopeCell({ row, value, changed, editable, locked, onChange }: {
  row: MatrixRow
  value: Scope | ''
  changed: boolean
  editable: boolean
  locked: boolean
  onChange: (value: Scope | '') => void
}) {
  const { t } = useApp()
  const label = `${resourceLabel(t, row.resource)} · ${actionLabel(t, row.action)}`
  const word = value ? t(`admin.scope.${value}`) : '—'
  const inherited = row.inheritedScope && (
    <span className="adm-inherited">{t('adm.roles.inheritedShort', { scope: t(`admin.scope.${row.inheritedScope}`) })}</span>
  )
  if (locked) {
    return (
      <span className={`scope ${SCOPE_CLASS[value]} is-locked`} aria-disabled="true" aria-label={`${label}: ${word}`}>
        <Icon name="lock" size="xs" />
        {word}
      </span>
    )
  }
  if (!editable) {
    return (
      <>
        <span className={value === '' ? 'scope-ro muted' : value === 'all' ? 'scope-ro strong' : 'scope-ro'}
              aria-label={`${label}: ${value ? word : t('adm.roles.noRight')}`}>
          {word}
        </span>
        {inherited}
      </>
    )
  }
  return (
    <>
      <select
        className={`scope ${SCOPE_CLASS[value]}${changed ? ' is-changed' : ''}`}
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value as Scope | '')}
      >
        <option value="">{t('adm.roles.noRight')}</option>
        {SCOPES.map((scope) => (
          <option key={scope} value={scope}>{t(`admin.scope.${scope}`)}</option>
        ))}
      </select>
      {inherited}
    </>
  )
}

function RecordMatrix({ rows, scopes, original, editable, locked, onChange }: {
  rows: MatrixRow[]
  scopes: Draft
  original: Draft
  editable: boolean
  locked: boolean
  onChange: (permissionId: string, value: Scope | '') => void
}) {
  const { t } = useApp()
  const actions = useMemo(() => {
    const present = [...new Set(rows.map((row) => row.action))]
    return [
      ...ACTION_ORDER.filter((action) => present.includes(action)),
      ...present.filter((action) => !ACTION_ORDER.includes(action)).sort(),
    ]
  }, [rows])
  const groups = useMemo(() => {
    const byResource = new Map<string, Map<string, MatrixRow>>()
    for (const row of rows) {
      const cells = byResource.get(row.resource) ?? new Map<string, MatrixRow>()
      cells.set(row.action, row)
      byResource.set(row.resource, cells)
    }
    const bySection = new Map<string, [string, Map<string, MatrixRow>][]>()
    for (const entry of byResource) {
      const key = sectionKey(entry[0])
      bySection.set(key, [...(bySection.get(key) ?? []), entry])
    }
    return SECTION_ORDER.filter((key) => bySection.has(key)).map((key) => [key, bySection.get(key)!] as const)
  }, [rows])

  if (rows.length === 0) {
    return <StateView boxed kind="empty" title={t('adm.roles.noPermissions')} />
  }
  return (
    <>
      <div className="scope-legend">
        <span><span className="strong">—</span> {t('adm.roles.legend.none')}</span>
        <span><span className="strong">{t('admin.scope.own')}</span> — {t('adm.roles.legend.own')}</span>
        <span><span className="strong">{t('admin.scope.department')}</span> — {t('adm.roles.legend.department')}</span>
        <span><span className="strong">{t('admin.scope.all')}</span> — {t('adm.roles.legend.all')}</span>
        {editable && (
          <span className="row" style={{ gap: 4 }}><span className="chg" style={{ height: 18 }}>●</span>{t('adm.roles.legend.changed')}</span>
        )}
      </div>
      <div className="tbl-wrap">
        <table className="tbl perm" aria-label={t('adm.roles.tab.records')}>
          <thead>
            <tr>
              <th>{t('admin.roles.resource')}</th>
              {actions.map((action) => (
                <th key={action} className="act">{actionLabel(t, action)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.flatMap(([section, resources]) => [
              <tr key={section} className="perm-group"><td colSpan={actions.length + 1}>{t(section)}</td></tr>,
              ...resources.map(([resource, cells]) => (
                <tr key={resource}>
                  <td style={{ paddingLeft: 16 }}>{resourceLabel(t, resource)}</td>
                  {actions.map((action) => {
                    const row = cells.get(action)
                    return (
                      <td key={action} className="cell">
                        {row && (
                          <ScopeCell row={row} value={scopes[row.permissionId] ?? ''}
                                     changed={(scopes[row.permissionId] ?? '') !== (original[row.permissionId] ?? '')}
                                     editable={editable} locked={locked}
                                     onChange={(value) => onChange(row.permissionId, value)} />
                        )}
                      </td>
                    )
                  })}
                </tr>
              )),
            ])}
          </tbody>
        </table>
      </div>
    </>
  )
}

/**
 * Полевые права поверх прав на записи: запись видна, а отдельное поле — нет. Здесь это
 * «включено / выключено»: область видимости поля задаёт право на саму запись.
 */
function FieldRights({ rows, scopes, editable, locked, onChange }: {
  rows: MatrixRow[]
  scopes: Draft
  editable: boolean
  locked: boolean
  onChange: (permissionId: string, value: Scope | '') => void
}) {
  const { t } = useApp()
  if (rows.length === 0) return <StateView boxed kind="empty" title={t('adm.roles.noFieldRights')} />
  return (
    <div className="panel">
      <div className="panel-h">
        <span className="h2">{t('adm.roles.tab.fields')}</span>
        <span className="count">{t('adm.roles.fields.sub')}</span>
      </div>
      {rows.map((row) => {
        const value = scopes[row.permissionId] ?? ''
        const on = value !== '' || Boolean(row.inheritedScope)
        const title = `${resourceLabel(t, row.resource)} · ${fieldLabel(t, row.field ?? '')}`
        return (
          <div className="list-item" key={row.permissionId}>
            <div className="stack" style={{ flex: 1, gap: 0 }}>
              <span style={{ fontWeight: 500 }}>{title}</span>
              <span className="t-sm muted">
                {actionLabel(t, row.action)}
                {row.inheritedScope && ` · ${t('adm.roles.inheritedShort', { scope: t(`admin.scope.${row.inheritedScope}`) })}`}
              </span>
            </div>
            {locked ? (
              <Switch checked disabled label={t('adm.roles.systemOn')} ariaLabel={title} onChange={() => undefined} />
            ) : editable ? (
              <Switch checked={value !== ''} ariaLabel={title}
                      onChange={(checked) => onChange(row.permissionId, checked ? (row.inheritedScope ?? 'all') : '')} />
            ) : (
              <OnOffBadge on={on} />
            )}
          </div>
        )
      })}
      <div className="panel-f muted">{t('adm.roles.fields.note')}</div>
    </div>
  )
}

function RoleUsers({ role }: { role: AccessRole }) {
  const { t, locale, user } = useApp()
  const allowed = can(user, 'system.user', 'read')
  const users = useQuery({
    queryKey: ['admin-users'],
    enabled: allowed,
    queryFn: ({ signal }) => fetchAccessUsers('', signal),
  })
  if (!allowed) {
    return <StateView boxed kind="forbidden" title={t('adm.roles.users.forbidden')} description={t('adm.roles.users.forbidden.text')} />
  }
  const holders = (users.data ?? []).flatMap((candidate) => {
    const grant = candidate.roles.find((assignment) => assignment.roleId === role.id)
    return grant ? [{ user: candidate, grant }] : []
  })
  return (
    <AdminState isLoading={users.isLoading} error={users.error} onRetry={() => void users.refetch()}>
      {holders.length === 0 ? (
        <StateView boxed kind="empty" title={t('adm.roles.users.empty')} />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl" aria-label={t('adm.roles.tab.users')}>
            <thead>
              <tr>
                <th>{t('adm.roles.users.user')}</th>
                <th>{t('admin.users.employee')}</th>
                <th>{t('adm.roles.users.term')}</th>
              </tr>
            </thead>
            <tbody>
              {holders.map(({ user: holder, grant }) => (
                <tr key={holder.id} className={holder.active ? undefined : 'is-muted'}>
                  <td>
                    <span className="row" style={{ gap: 8 }}>
                      <span className="ava">{initials(holder.fullName)}</span>
                      {holder.fullName}
                      <span className="mono t-sm muted">{holder.username}</span>
                    </span>
                  </td>
                  <td className="secondary">{holder.employeeName ?? '—'}</td>
                  <td>
                    {grant.expiresAt ? (
                      <span className={grant.expired ? 'badge' : 'badge b-warning'}>
                        {grant.expired
                          ? t('admin.users.expired')
                          : t('admin.users.until', { date: new Date(grant.expiresAt).toLocaleDateString(locale === 'en' ? 'en-GB' : 'ru-RU') })}
                      </span>
                    ) : (
                      <span className="secondary">{t('adm.roles.users.forever')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminState>
  )
}

function NewRoleModal({ roles, copyOf, onClose, onSaved }: {
  roles: AccessRole[]
  copyOf: AccessRole | null
  onClose: () => void
  onSaved: (role: AccessRole) => void
}) {
  const { t } = useApp()
  const formId = useId()
  const [draft, setDraft] = useState({
    code: copyOf ? `${copyOf.code}_COPY` : '',
    name: copyOf ? t('adm.roles.copyName', { name: copyOf.name }) : '',
    parentId: copyOf?.parentId ?? '',
  })
  const [error, setError] = useState<string | null>(null)
  const create = useMutation({
    mutationFn: async () => {
      const role = await createAccessRole({ code: draft.code, name: draft.name, parentId: draft.parentId || null })
      if (copyOf) {
        // A copy takes the source's direct grants; inherited ones come with the same parent.
        const source = await fetchRoleMatrix(copyOf.id)
        await saveRoleMatrix(
          role.id,
          source.filter((row) => row.scope).map((row) => ({ permissionId: row.permissionId, scope: row.scope as Scope })),
        )
      }
      return role
    },
    onSuccess: onSaved,
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })
  return (
    <EditLayer
      kind="modal"
      overline={t('adm.roles.overline')}
      title={copyOf ? t('adm.roles.copyTitle', { name: copyOf.name }) : t('admin.roles.form')}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('adm.roles.create')} pending={create.isPending} form={formId} />}
    >
      <form id={formId} className="form-grid" aria-label={t('admin.roles.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        create.mutate()
      }}>
        <Field label={t('admin.roles.name')} htmlFor="nr-name" required span2>
          <input id="nr-name" className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </Field>
        <Field label={t('admin.roles.code')} htmlFor="nr-code" required help={t('adm.roles.codeHelp')}>
          <input id="nr-code" className="input mono" value={draft.code}
                 onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })} />
        </Field>
        <Field label={t('admin.roles.parent')} htmlFor="nr-parent" help={t('adm.roles.parentHelp')}>
          <select id="nr-parent" className="input" value={draft.parentId} onChange={(e) => setDraft({ ...draft, parentId: e.target.value })}>
            <option value="">{t('admin.roles.noParent')}</option>
            {roles.filter((role) => role.code !== SUPERADMIN).map((role) => (
              <option key={role.id} value={role.id}>{role.name} ({role.code})</option>
            ))}
          </select>
        </Field>
        {error && <div className="span-2"><Notice text={error} /></div>}
      </form>
    </EditLayer>
  )
}

function RenameRoleModal({ role, onClose, onSaved }: { role: AccessRole; onClose: () => void; onSaved: () => void }) {
  const { t } = useApp()
  const formId = useId()
  const [name, setName] = useState(role.name)
  const [description, setDescription] = useState(role.description ?? '')
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () => updateAccessRole(role.id, { name, description }),
    onSuccess: onSaved,
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })
  return (
    <EditLayer
      kind="modal"
      large={false}
      overline={t('adm.roles.overline')}
      title={t('adm.roles.rename')}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('admin.save')} pending={save.isPending} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('adm.roles.rename')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <Field label={t('admin.roles.name')} htmlFor="rr-name" required help={t('adm.roles.renameHelp', { name: role.name })}>
          <input id="rr-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('adm.roles.description')} htmlFor="rr-desc">
          <textarea id="rr-desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
